import { useEffect, useRef, useState } from "react"

import CheckCircle from "@/assets/img/soulmate/processing/check_circle_micro.svg"
import Ellipse from "@/assets/img/soulmate/processing/ellips.svg"
import FeaturesIcon from "@/assets/img/soulmate/processing/features.svg"
import LeftLine from "@/assets/img/soulmate/processing/line_corner-left.svg"
import MiddleLine from "@/assets/img/soulmate/processing/line_corner-middle.svg"
import RightLine from "@/assets/img/soulmate/processing/line_corner-right.svg"
import LeftLineDone from "@/assets/img/soulmate/processing/line_corner_left_finished.svg"
import MiddleLineDone from "@/assets/img/soulmate/processing/line_corner_middle_finished.svg"
import RightLineDone from "@/assets/img/soulmate/processing/line_corner_right_finished.svg"
import ExpressionIcon from "@/assets/img/soulmate/processing/expression.svg"
import FaceShapeIcon from "@/assets/img/soulmate/processing/face-shape.svg"
import StarsBg from "@/assets/img/soulmate/proc-bg.png"
import Selfie from "@/assets/img/soulmate/proc-selfie.png"
import { AnimatedFaceMesh } from "@/packages/face-mesh"
import { RadialDottedProgress, useProcessingProgress } from "@/packages/processing"
import { cn } from "@/lib/utils"
import { useMediaStore } from "@/stores/media-store"
import type { StepComponentProps } from "./step-contract"

interface Stage {
  label: string
  threshold: number
  icon: string
  line: string
  lineDone: string
  lineW: number
  lineH: number
}

/** Face-scan stages (origene FaceProcessing), passed in order as progress rises. */
const STAGES: Stage[] = [
  { label: "Face shape", threshold: 33, icon: FaceShapeIcon, line: LeftLine, lineDone: LeftLineDone, lineW: 36, lineH: 56 },
  { label: "Expression", threshold: 66, icon: ExpressionIcon, line: MiddleLine, lineDone: MiddleLineDone, lineW: 36, lineH: 26 },
  { label: "Key features", threshold: 100, icon: FeaturesIcon, line: RightLine, lineDone: RightLineDone, lineW: 36, lineH: 56 },
]

const PHASES = STAGES.map((s) => ({ key: s.label, label: s.label }))

// Progress ring, as in origene (and the design, which draws it white — not green):
// a faint neutral track that fills with a brighter near-white. Green stays for the
// stage rings/checkmarks, which is the only place the design uses it.
const RING_FILLED = "#d9d9d9"
const RING_TRACK = "rgba(241, 242, 243, 0.2)"

/**
 * Ring metrics from the design, which draws it as a 16px dashed border set 16px
 * off the photo. RING_REACH is how far it extends past the photo box, so the
 * layout can reserve room and keep the ticks inside the column.
 */
const RING_OFFSET = 16
const RING_LINE_LENGTH = 16
const RING_REACH = RING_OFFSET + RING_LINE_LENGTH

/** The mobile-column width (`--container-app` / max-w-app, px). The capture screen
 *  shows the selfie inside this column, so the cover-zoom must be measured against
 *  it — not the full viewport — for the processing framing to match what was shot. */
function appColumnWidthPx(): number {
  if (typeof window === "undefined") return 440
  const raw = getComputedStyle(document.documentElement).getPropertyValue("--container-app")
  const n = parseInt(raw, 10)
  return Number.isFinite(n) && n > 0 ? n : 440
}

/**
 * Full-bleed Soulmate face-scan processing screen, ported 1:1 from origene
 * FaceProcessing: a rotating gradient backdrop, a row of three scan stages (a
 * spinning ellipse that becomes a green check), and the user's selfie in a tall
 * rounded oval with the animated face mesh replayed over it and a radial dotted
 * progress ring around it. Wired via the quiz's `stepOverrides.analysis`.
 *
 * Phased progress comes from `@/packages/processing`; the mesh from the captured
 * landmarks (`@/packages/face-mesh`); the photo is the real selfie/upload from
 * the media store, with the static design image as a fallback.
 */
export function SoulmateProcessingStep({ step, onAdvance }: StepComponentProps) {
  const realPhoto = useMediaStore((s) => s.photos.selfie)
  const photo = realPhoto ?? Selfie
  const mesh = useMediaStore((s) => s.meshes.selfie) ?? null
  const { progress } = useProcessingProgress(PHASES, { onFinish: onAdvance })
  const overall = progress.reduce((sum, p) => sum + p, 0) / PHASES.length

  // Match the selfie framing. The capture screen shows the photo object-cover in the
  // mobile column (max-w-app wide, full height → large cover zoom, the face fills the
  // oval). Here the oval box is much shorter, so the same object-cover zooms far less →
  // the face reads "far away" with shoulders. Re-zoom the photo (and the mesh, in
  // lockstep) by the ratio of the two cover scales, derived from the real frame/box/
  // camera-column sizes so it holds for any capture aspect (landscape or portrait). The
  // design fallback image is already framed, so it stays at zoom 1.
  const boxRef = useRef<HTMLDivElement>(null)
  const [zoom, setZoom] = useState(1)
  useEffect(() => {
    if (!realPhoto) return // design fallback is already framed — keep zoom at its initial 1
    const box = boxRef.current
    if (!box) return
    const img = new Image()
    let observer: ResizeObserver | null = null
    const recompute = () => {
      const nW = img.naturalWidth
      const nH = img.naturalHeight
      const cW = box.clientWidth
      const cH = box.clientHeight
      if (!nW || !nH || !cW || !cH) return
      const cameraWidth = Math.min(window.innerWidth, appColumnWidthPx())
      const coverSelfie = Math.max(cameraWidth / nW, window.innerHeight / nH)
      const coverBox = Math.max(cW / nW, cH / nH)
      setZoom(coverSelfie / coverBox)
    }
    img.onload = () => {
      recompute()
      observer = new ResizeObserver(recompute)
      observer.observe(box)
      window.addEventListener("resize", recompute)
    }
    img.src = realPhoto
    return () => {
      observer?.disconnect()
      window.removeEventListener("resize", recompute)
    }
  }, [realPhoto])

  return (
    <div className="relative flex min-h-[100dvh] flex-col items-center justify-center overflow-hidden bg-dark-base px-5 py-5 text-purple-light">
      {/* Backdrop: flat `--dark-base` (the design's Dark Palette/50 = #0d081c) with
          the starfield pinned to the top at its design size, 414×240 centred and
          cropped with object-cover. The design has no rotating gradient — the one
          ported from origene tinted the whole column, and on a wide screen it read
          as a lighter background with a visible edge where the image stopped. */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden">
        <img
          src={StarsBg}
          alt=""
          className="absolute top-0 left-1/2 h-[240px] w-[414px] max-w-full -translate-x-1/2 object-cover"
        />
      </div>

      {/* Caption */}
      <p className="relative z-10 mb-5 max-w-[300px] text-center font-body text-sm leading-4 text-white/60">
        {step.title}
      </p>

      {/* Stage row */}
      <div className="relative z-10 flex w-[calc(100%-60px)] items-start justify-between">
        {STAGES.map((stage) => (
          <StageItem key={stage.label} stage={stage} done={overall >= stage.threshold} />
        ))}
      </div>

      {/* Photo + animated mesh + radial progress */}
      {/* No `items-center`: the box has to STRETCH to this row's height (that is what
          lets the stadium's straight middle grow), so the cross-axis default applies.
          The padding reserves room for the ring, which is drawn outside the box. */}
      <div
        className="relative z-10 flex w-full flex-1 justify-center"
        style={{ padding: RING_REACH }}
      >
        {/* A stadium, NOT an ellipse: the design draws this as a `rounded-[999px]`
            box, so the caps are half-circles joined by straight sides. Width and
            height are set INDEPENDENTLY (as in origene) rather than locked to an
            aspect ratio — the corner radius clamps to half the SHORTER side, which
            for this tall box is half the width, so growing the height only stretches
            the straight middle and leaves the caps' radius untouched. Height comes
            from the leftover space (`flex-1` on the parent, `h-full` here).
            The ring is drawn OUTSIDE this box and reaches RING_REACH past it, so the
            parent reserves that much padding; width tracks the `max-w-app` column via
            `100%` (a `100vw` budget never bound on desktop and let the ring escape). */}
        <div className="relative w-full">
          <div ref={boxRef} className="absolute inset-0 overflow-hidden rounded-full">
            <div className="absolute inset-0" style={{ transform: `scale(${zoom})` }}>
              <img src={photo} alt="" className="absolute inset-0 size-full object-cover" />
              {/* Animated face mesh (mirrored in-draw); no-op for uploads (no mesh). */}
              <AnimatedFaceMesh
                capture={mesh}
                durationMs={8500}
                cycles={6}
                className="pointer-events-none absolute inset-0 size-full object-cover"
              />
            </div>
          </div>
          <RadialDottedProgress
            progress={overall}
            filledColor={RING_FILLED}
            emptyColor={RING_TRACK}
            offset={RING_OFFSET}
            lineLength={RING_LINE_LENGTH}
          />
        </div>
      </div>
    </div>
  )
}

/** One scan stage: a 56px ring (spinning ellipse → green check) with icon, label, line. */
function StageItem({ stage, done }: { stage: Stage; done: boolean }) {
  return (
    <div className="flex flex-col items-center text-center">
      <span className="relative flex size-14 items-center justify-center">
        {done ? (
          <>
            <span className="absolute inset-0 rounded-full border-2 border-green shadow-[0_0_8px_var(--green)]" />
            <img src={CheckCircle} alt="" className="absolute -top-px -right-px size-[18px]" />
          </>
        ) : (
          <img
            src={Ellipse}
            alt=""
            className="absolute inset-0 size-14 animate-[spin_1.6s_linear_infinite] object-contain"
          />
        )}
        <img src={stage.icon} alt="" className="size-9" />
      </span>
      <span className="mt-1.5 font-body text-sm leading-[14px] text-white/60">{stage.label}</span>
      <img
        src={done ? stage.lineDone : stage.line}
        alt=""
        width={stage.lineW}
        height={stage.lineH}
        className={cn("mt-2", done && "")}
      />
    </div>
  )
}
